/**
 * Phase J0.20 — Auth Token Central Store (Fév 2026).
 * Phase J1.09 — Migration sessionStorage → localStorage pour PWA offline.
 *
 * Centralise TOUS les accès aux tokens admin/employee en un seul module.
 * Bénéfices :
 *  1. Sécurité — 1 seul lieu à migrer pour passer plus tard en httpOnly cookie
 *     (durcissement XSS sans toucher 20 fichiers).
 *  2. Stabilité — wrappers null-safe : pas de crash sur localStorage indispo
 *     (mode privé Safari iOS, quotaExceededError, etc.).
 *  3. Cohérence — une seule clé `admin_token` / `admin_user` / `employee_token`
 *     / `employee` partout (avant : 8 endroits avec strings hardcodés).
 *  4. PWA offline — `localStorage` persiste après fermeture de l'app (vs
 *     `sessionStorage` qui était vidé à chaque close → impossible de se
 *     reconnecter hors-ligne sur iPhone PWA installé).
 *
 * Usage:
 *   import { getAdminToken, setAdminSession, clearAdminSession } from "@/lib/auth";
 *
 * Iter 70 (Fév 2026) — Les `catch` étaient silencieux. Ils logguent désormais
 * en dev pour aider le debug, et restent silencieux en prod pour ne pas
 * exposer d'info sensible / polluer la console.
 */

const KEYS = {
  ADMIN_TOKEN: "admin_token",
  ADMIN_USER: "admin_user",
  EMPLOYEE_TOKEN: "employee_token",
  EMPLOYEE_USER: "employee",
};

const IS_DEV =
  typeof process !== "undefined" &&
  process.env &&
  process.env.NODE_ENV !== "production";

/** Conditional logger — silent in production, verbose in dev. */
function _warn(scope, err) {
  if (!IS_DEV) return;
  try {
    console.warn(`[auth:${scope}]`, err?.message || err);
  } catch (_e) {
    /* console itself unavailable — give up silently */
  }
}

// --- Safe storage helpers (resilient to private mode / quota errors) -------
// Phase J1.09 : on utilise localStorage (persiste après fermeture app PWA)
// au lieu de sessionStorage (qui était effacé à chaque close, rendant l'app
// inutilisable en mode avion sur iPhone PWA installé).

function _store() {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage;
    }
  } catch (err) {
    _warn("store", err); /* iOS private mode throws on access */
  }
  return null;
}

/**
 * Migration one-shot des anciens tokens stockés en sessionStorage vers
 * localStorage. Lancée au chargement du module pour ne pas déconnecter
 * les utilisateurs déjà loggés au moment du déploiement de J1.09.
 */
function _migrateFromSessionStorage() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    const ls = window.localStorage;
    if (!ls) return;
    Object.values(KEYS).forEach((k) => {
      const fromSession = window.sessionStorage.getItem(k);
      if (fromSession && !ls.getItem(k)) {
        ls.setItem(k, fromSession);
      }
    });
  } catch (err) {
    _warn("migrate", err);
  }
}

_migrateFromSessionStorage();

function _get(key) {
  try {
    const s = _store();
    return s ? s.getItem(key) : null;
  } catch (err) {
    _warn("get", err);
    return null;
  }
}

function _set(key, value) {
  try {
    const s = _store();
    if (s) s.setItem(key, value);
  } catch (err) {
    _warn("set", err); /* quota / serialization failure */
  }
}

function _remove(key) {
  try {
    const s = _store();
    if (s) s.removeItem(key);
  } catch (err) {
    _warn("remove", err);
  }
}

// --- Admin session ---------------------------------------------------------

export function getAdminToken() {
  return _get(KEYS.ADMIN_TOKEN) || "";
}

export function getAdminUser() {
  const raw = _get(KEYS.ADMIN_USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (err) {
    _warn("parse-admin-user", err);
    return null;
  }
}

export function setAdminSession({ token, user }) {
  if (token) _set(KEYS.ADMIN_TOKEN, token);
  if (user) {
    try {
      _set(KEYS.ADMIN_USER, JSON.stringify(user));
    } catch (err) {
      _warn("stringify-admin-user", err);
    }
  }
}

export function clearAdminSession() {
  _remove(KEYS.ADMIN_TOKEN);
  _remove(KEYS.ADMIN_USER);
}

// --- Employee session ------------------------------------------------------

export function getEmployeeToken() {
  return _get(KEYS.EMPLOYEE_TOKEN) || "";
}

export function getEmployeeUser() {
  const raw = _get(KEYS.EMPLOYEE_USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (err) {
    _warn("parse-employee-user", err);
    return null;
  }
}

export function setEmployeeSession({ token, user }) {
  if (token) _set(KEYS.EMPLOYEE_TOKEN, token);
  if (user) {
    try {
      _set(KEYS.EMPLOYEE_USER, JSON.stringify(user));
    } catch (err) {
      _warn("stringify-employee-user", err);
    }
  }
}

export function clearEmployeeSession() {
  _remove(KEYS.EMPLOYEE_TOKEN);
  _remove(KEYS.EMPLOYEE_USER);
}

// --- Aggregate ----------------------------------------------------------

/** Returns the active token (admin first, fallback employee). */
export function getActiveToken() {
  return getAdminToken() || getEmployeeToken() || "";
}

export function clearAllSessions() {
  clearAdminSession();
  clearEmployeeSession();
}

// --- Validité JWT (Oct 2026) ------------------------------------------------
// Fix bug « j'accède au dashboard puis erreur non identifié » : un token
// EXPIRÉ restait en localStorage et l'UI le considérait comme une session
// valide. On décode le payload (sans vérifier la signature — le backend s'en
// charge) juste pour lire `exp` et refuser les tokens périmés dès le boot.

/** true si le token JWT est présent ET non expiré (marge 30 s). */
export function isJwtValid(token) {
  if (!token) return false;
  try {
    const part = token.split(".")[1];
    if (!part) return false;
    const payload = JSON.parse(
      atob(part.replace(/-/g, "+").replace(/_/g, "/")),
    );
    if (!payload.exp) return true; // pas d'expiration → on laisse le backend juger
    return payload.exp * 1000 > Date.now() + 30_000;
  } catch (_e) {
    return false;
  }
}

/** Session admin valide ? Purge le token s'il est expiré. */
export function hasValidAdminSession() {
  const token = getAdminToken();
  if (!token) return false;
  if (isJwtValid(token)) return true;
  clearAdminSession();
  return false;
}

/** Session salarié valide ? Purge le token s'il est expiré. */
export function hasValidEmployeeSession() {
  const token = getEmployeeToken();
  if (!token) return false;
  if (isJwtValid(token)) return true;
  clearEmployeeSession();
  return false;
}

// Export keys for tests / debugging only
export const AUTH_KEYS = KEYS;
